import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { waLink } from "@/lib/whatsapp";

const LINKS = [
    { label: "Início", href: "#inicio", testid: "nav-inicio" },
    { label: "Experiências", href: "#experiencias", testid: "nav-experiencias" },
    { label: "LC Cerimonial", href: "#cerimonial", testid: "nav-cerimonial" },
    { label: "AstroLive", href: "#astrolive", testid: "nav-astrolive" },
    { label: "Sobre", href: "#sobre", testid: "nav-sobre" },
    { label: "Contato", href: "#contato", testid: "nav-contato" },
];

export const scrollToHash = (hash) => {
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, href) => {
        e.preventDefault();
        setOpen(false);
        setTimeout(() => scrollToHash(href), open ? 350 : 0);
    };

    return (
        <>
            <header
                data-testid="main-header"
                className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled
                        ? "bg-white/85 backdrop-blur-xl border-b border-black/10"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="flex items-center justify-between px-6 md:px-12 py-5">
                    <a
                        href="#inicio"
                        onClick={(e) => go(e, "#inicio")}
                        data-testid="header-logo"
                        className="flex items-baseline gap-2 group"
                    >
                        <span className="font-editorial italic text-3xl leading-none text-[#6A2022]">LC</span>
                        <span className="text-[10px] tracking-[0.4em] uppercase text-[#0A0A0A]/70 group-hover:text-[#0A0A0A] transition-colors duration-300">
                            Production
                        </span>
                    </a>

                    <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={(e) => go(e, l.href)}
                                data-testid={l.testid}
                                className="relative text-[11px] tracking-[0.25em] uppercase text-[#0A0A0A]/55 hover:text-[#0A0A0A] transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#6A2022] after:transition-[width] after:duration-300 hover:after:w-full"
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-4">
                        <a
                            href={waLink("geral")}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-cta-whatsapp"
                            className="hidden sm:inline-flex items-center gap-2 border border-black/30 px-6 py-2.5 text-[11px] tracking-[0.25em] uppercase text-[#0A0A0A] transition-colors duration-300 hover:bg-[#6A2022] hover:border-[#6A2022] hover:text-white"
                        >
                            Solicitar Orçamento
                            <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="mobile-menu-open"
                            aria-label="Abrir menu"
                            className="lg:hidden text-[#0A0A0A] p-1"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-[60] bg-white flex flex-col"
                        data-testid="mobile-menu"
                    >
                        <div className="flex items-center justify-between px-6 py-5 border-b border-black/10">
                            <span className="font-editorial italic text-3xl text-[#6A2022]">LC</span>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="mobile-menu-close"
                                aria-label="Fechar menu"
                                className="text-[#0A0A0A] p-1"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <nav className="flex flex-col justify-center flex-1 px-8 gap-2">
                            {LINKS.map((l, i) => (
                                <motion.a
                                    key={l.href}
                                    href={l.href}
                                    onClick={(e) => go(e, l.href)}
                                    data-testid={`mobile-${l.testid}`}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                    className="py-3 text-3xl font-extrabold uppercase tracking-tight text-[#0A0A0A]/85 hover:text-[#6A2022] transition-colors duration-300 border-b border-black/5"
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                        </nav>
                        <div className="px-8 pb-10">
                            <a
                                href={waLink("geral")}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-menu-cta-whatsapp"
                                className="flex items-center justify-center gap-2 bg-[#6A2022] text-white py-4 text-xs tracking-[0.3em] uppercase"
                            >
                                Solicitar Orçamento
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header;
